import { useEffect, useState, type FormEvent } from 'react';

import {
  listLocations,
  type CauseType,
  type LocationNode,
  type Task,
  type TaskPriority,
} from '../api.js';
import { useProjectMembers } from './ProjectMembers.js';
import { CAUSE_LABELS, PRIORITIES, PRIORITY_LABELS } from './task-labels.js';

/**
 * The two forms behind the task list: writing a task, and blocking one.
 *
 * `BlockerForm` exists because **a task cannot be blocked without a reason**
 * (`05` §Task): the cause and the description are written in the same
 * transaction as the status move, so there is no moment where a blocked task
 * has no explanation.
 */
interface FormProps {
  readonly projectId: string;
  readonly task?: Task;
  readonly busy: boolean;
  readonly onCancel: () => void;
  /** Lets the drawer ask before throwing away what has been typed. */
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (input: {
    title: string;
    description: string | null;
    owner_id: string | null;
    priority: TaskPriority;
    due_date: string | null;
    location_id: string | null;
    needs_photo_evidence: boolean;
  }) => void | Promise<void>;
}

export function TaskForm({
  projectId,
  task,
  busy,
  onCancel,
  onDirtyChange,
  onSubmit,
}: FormProps): JSX.Element {
  const [title, setTitle] = useState(task?.title ?? '');
  const [description, setDescription] = useState(task?.description ?? '');
  const [ownerId, setOwnerId] = useState(task?.owner_id ?? '');
  const [priority, setPriority] = useState<TaskPriority>(task?.priority ?? 'normal');
  const [dueDate, setDueDate] = useState(task?.due_date ?? '');
  const [locationId, setLocationId] = useState(task?.location_id ?? '');
  const [needsPhoto, setNeedsPhoto] = useState(task?.needs_photo_evidence ?? false);

  /*
   * Compared against the record rather than tracked with a touched flag:
   * somebody who types a character and deletes it has changed nothing. The
   * priority starts at a real value, so it is compared against that.
   */
  useEffect(() => {
    onDirtyChange?.(
      title !== (task?.title ?? '') ||
        description !== (task?.description ?? '') ||
        ownerId !== (task?.owner_id ?? '') ||
        priority !== (task?.priority ?? 'normal') ||
        dueDate !== (task?.due_date ?? '') ||
        locationId !== (task?.location_id ?? '') ||
        needsPhoto !== (task?.needs_photo_evidence ?? false),
    );
  }, [title, description, ownerId, priority, dueDate, locationId, needsPhoto, task, onDirtyChange]);
  /** The page's one member fetch — its own when this renders standalone. */
  const members = useProjectMembers(projectId);
  const [locations, setLocations] = useState<readonly LocationNode[]>([]);

  // Both lists are optional context, not the point of the form — if either
  // fails, the dropdown is simply empty and the task can still be saved.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const tree = await listLocations(projectId);
        if (!cancelled) setLocations(flatten(tree));
      } catch {
        /* leave the location list empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!title.trim()) return;
    void onSubmit({
      title: title.trim(),
      description: description.trim() || null,
      owner_id: ownerId || null,
      priority,
      due_date: dueDate || null,
      location_id: locationId || null,
      needs_photo_evidence: needsPhoto,
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="field">
        <label htmlFor={`task-title-${task?.id ?? 'new'}`}>What needs doing</label>
        <input
          id={`task-title-${task?.id ?? 'new'}`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          disabled={busy}
          placeholder="Set out partitions on Level 3"
        />
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor={`task-owner-${task?.id ?? 'new'}`}>Who</label>
          <select
            id={`task-owner-${task?.id ?? 'new'}`}
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            disabled={busy}
          >
            <option value="">Nobody yet</option>
            {members.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor={`task-priority-${task?.id ?? 'new'}`}>Priority</label>
          <select
            id={`task-priority-${task?.id ?? 'new'}`}
            value={priority}
            onChange={(e) => setPriority(e.target.value as TaskPriority)}
            disabled={busy}
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABELS[p]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor={`task-due-${task?.id ?? 'new'}`}>Due</label>
          <input
            id={`task-due-${task?.id ?? 'new'}`}
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            disabled={busy}
          />
        </div>

        <div className="field">
          <label htmlFor={`task-location-${task?.id ?? 'new'}`}>Where</label>
          <select
            id={`task-location-${task?.id ?? 'new'}`}
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            disabled={busy}
          >
            <option value="">Nowhere in particular</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.path}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        <label htmlFor={`task-desc-${task?.id ?? 'new'}`}>Notes (optional)</label>
        <textarea
          id={`task-desc-${task?.id ?? 'new'}`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          maxLength={5000}
          disabled={busy}
        />
      </div>

      {/*
        The user's rule, 2026-08-20, as a PER-TASK decision rather than a global
        one. Whoever raises the task knows whether there is anything to
        photograph: "fit the ceiling grid" gets ticked, "chase the consultant
        for the RCP" does not — and a rule people cannot satisfy is a rule they
        work around.
      */}
      <div className="field">
        <label htmlFor={`task-photo-${task?.id ?? 'new'}`}>
          <input
            id={`task-photo-${task?.id ?? 'new'}`}
            type="checkbox"
            checked={needsPhoto}
            onChange={(e) => setNeedsPhoto(e.target.checked)}
            disabled={busy}
          />{' '}
          Needs a photo before it can be finished
        </label>
        <p className="hint">
          For work somebody has to see done. It cannot be marked finished until a photo is on it —
          taken from On site, or attached to the task.
        </p>
      </div>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !title.trim()}>
          {busy ? 'Saving…' : task ? 'Save' : 'Add task'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

interface BlockerFormProps {
  readonly busy: boolean;
  readonly onCancel: () => void;
  /** Lets the drawer ask before throwing away what has been typed. */
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (input: {
    cause_type: CauseType;
    description: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
  }) => void | Promise<void>;
}

/**
 * Asking why, before marking something blocked.
 *
 * The server refuses a block with no reason, so this is not politeness — it is
 * the only way through. Which is the point: "blocked" with no explanation tells
 * a PM nothing they can act on.
 */
export function BlockerForm({
  busy,
  onCancel,
  onDirtyChange,
  onSubmit,
}: BlockerFormProps): JSX.Element {
  const [causeType, setCauseType] = useState<CauseType>('approval');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');

  // The cause and the severity start at real values, so they are compared
  // against those rather than against nothing.
  useEffect(() => {
    onDirtyChange?.(description !== '' || causeType !== 'approval' || severity !== 'medium');
  }, [description, causeType, severity, onDirtyChange]);

  return (
    <div className="panel blocker-form">
      <p className="panel-title">What is stopping it?</p>

      <div className="field-row">
        <div className="field">
          <label htmlFor="blocker-cause">Waiting on</label>
          <select
            id="blocker-cause"
            value={causeType}
            onChange={(e) => setCauseType(e.target.value as CauseType)}
            disabled={busy}
          >
            {(Object.keys(CAUSE_LABELS) as CauseType[]).map((key) => (
              <option key={key} value={key}>
                {CAUSE_LABELS[key]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="blocker-severity">How bad</label>
          <select
            id="blocker-severity"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as typeof severity)}
            disabled={busy}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>
      </div>

      <div className="field">
        <label htmlFor="blocker-description">Details</label>
        <textarea
          id="blocker-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          maxLength={2000}
          disabled={busy}
          placeholder="Waiting on the ceiling detail approval from Arcadia."
        />
        <p className="hint">Whoever picks this up needs to know who to chase.</p>
      </div>

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || !description.trim()}
          onClick={() =>
            void onSubmit({ cause_type: causeType, description: description.trim(), severity })
          }
        >
          {busy ? 'Saving…' : 'Mark blocked'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** The location tree, flattened — a dropdown cannot nest. */
function flatten(tree: readonly LocationNode[]): LocationNode[] {
  const out: LocationNode[] = [];
  for (const node of tree) {
    out.push(node);
    out.push(...flatten(node.children));
  }
  return out;
}

// ---------------------------------------------------------------------------
// The board — the same rows, arranged by where they stand
// ---------------------------------------------------------------------------

